<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import Title from "@/components/Title/Title.vue";
import { Search } from '@element-plus/icons-vue'
import axios from "axios";
import {useStore} from "vuex";
import dayjs from "dayjs";
import SvgIcon from "@/components/SvgIcon/SvgIcon.vue";
import MoreButton from "@/pages/Index/components/MoreButton.vue";
import ResumeUploadDialog from "@/pages/BusinessCooperation/components/ResumeUploadDialog.vue";
import {useWindowResize} from "@/utils";

const store = useStore()
const { windowSize, handleWindowResizeWithDebounce } = useWindowResize()

const SOCIAL_RECRUITMENT = 1
const CAMPUS_RECRUITMENT = 2

const currentJobType = ref(1)
const searchValue = ref('')
const jobList = ref([])
const currentCollapse = ref(null)
const dialogVisible = ref(false)
const position = ref(null)
const dialogWidth = ref('656px')

const currentLang = computed(() => store.getters.currentLanguage)

const getJobList = async () => {
	const params = {
		[`title_${currentLang.value}`]: searchValue.value,
		type: currentJobType.value
	}
	const res = await axios.get(`/api/api/talent/recruitment`, { params })
		.catch((err) => {
			throw new Error(err.message)
		})
	console.log('res --------->', res)
	jobList.value = res.data
}

const handleSearch = () => {
	getJobList()
}

const handleChangeJobType = (type) => {
	currentJobType.value = type
	getJobList()
}

const handleChangeCollapse = (id) => {
	if (id === currentCollapse.value) currentCollapse.value = null
	else currentCollapse.value = id
}

const generateJobContentBlocks = (item) => {
	return [
		{
			title: 'contact.job.Duty',
			blocks: item?.[`job_responsibilities_${currentLang.value}`]
		},
		{
			title: 'contact.job.JobRequirements',
			blocks: item?.[`job_requirements_${currentLang.value}`]
		},
		{
			title: 'contact.job.TreatmentAndWelfare',
			blocks: item?.[`benefits_${currentLang.value}`]
		}
	]
}

const headerIconColor = (id) => {
	return id === currentCollapse.value ? '#fff' : '#A8ABB2'
}

const handleUploadResume = (id) => {
	position.value = id
	dialogVisible.value = true
}

const isHaveJobInfo = (info) => {
	const len = info?.length
	
	if (len === 1 && !info?.[0]) return false
	
	return true
}

watch(
	() => currentLang.value,
	(n) => {
		searchValue.value = ''
		getJobList()
	}
)

watch(
	() => windowSize.value,
	(n) => {
		if (n >= 768) {
			dialogWidth.value = '656px'
		} else {
			dialogWidth.value = '80%'
		}
	},
	{
		immediate: true
	}
)

onMounted(() => {
	getJobList()
	window.addEventListener('resize', handleWindowResizeWithDebounce)
})

onUnmounted(() => {
	window.removeEventListener('resize', handleWindowResizeWithDebounce)
})
</script>

<template>
	<div class="job">
		
		<div class="job__header">
			<Title
				class="job__header__title"
				position="left"
				mode="dark"
				:title="$t('contact.job.title')"
			/>
			
			<div class="right">
				<div
					class="right__type"
					:class="{
						'right__type--active': currentJobType === 1
					}"
					@click="handleChangeJobType(SOCIAL_RECRUITMENT)"
				>
					{{ $t('contact.job.SocialRecruitment') }}
				</div>
				
				<div
					class="right__type"
					:class="{
						'right__type--active': currentJobType === 2
					}"
					@click="handleChangeJobType(CAMPUS_RECRUITMENT)"
				>
					{{ $t('contact.job.CampusRecruitment') }}
				</div>
				
				<div class="right__search">
					<el-input
						v-model="searchValue"
						size="large"
						:placeholder="$t('contact.job.search')"
						:prefix-icon="Search"
						@change="handleSearch"
					/>
				</div>
			</div>
		</div>
		
		<div class="job__list" v-if="jobList.length > 0">
			<div
				class="job__list__item item"
				v-for="(item, index) in jobList"
				:key="index"
			>
				<div
					class="item__header item__header--pc"
					:class="{
						'item__header--open': item.id === currentCollapse
					}"
				>
					<div
						class="item__header__left line-clamp-1"
						:title="item?.[`title_${currentLang}`] || item?.title_cn"
					>
						{{ item?.[`title_${currentLang}`] || item?.title_cn }}
					</div>
					
					<div class="item__header__center">
						<div class="info">
							<el-icon
								size="24px"
								:color="headerIconColor(item.id)"
							>
								<User />
							</el-icon>
							<div class="info__value">
								{{ item?.number_recruits }}{{ $t('contact.job.numberRecruitsUnit') }}
							</div>
						</div>
						
						<div class="info">
							<el-icon
								size="24px"
								:color="headerIconColor(item.id)"
							>
								<LocationInformation />
							</el-icon>
							<div class="info__value">
								{{ item?.[`region_${currentLang}`] }}
							</div>
						</div>
						
						<div class="info">
							<el-icon
								size="24px"
								:color="headerIconColor(item.id)"
							>
								<Coin />
							</el-icon>
							<div class="info__value">
								{{ item?.salary }}
							</div>
						</div>
						
						<div class="info">
							<el-icon
								size="24px"
								:color="headerIconColor(item.id)"
							>
								<Clock />
							</el-icon>
							<div class="info__value">
								{{ dayjs(item?.end_data).format('YYYY-MM-DD') }}
							</div>
						</div>
					</div>
					
					<div
						class="item__header__right"
						:class="{
						'item__header__right--open': item.id === currentCollapse
					}"
						@click="handleChangeCollapse(item.id)"
					>
						<SvgIcon
							:name="item.id === currentCollapse ? 'circleArrow-white' : 'circleArrow'"
							class-name="icon"
						/>
					</div>
				</div>
				
				<div
					class="item__header item__header--mobile"
					:class="{
						'item__header--open': item.id === currentCollapse
					}"
				>
					<div class="flex-between">
						<div
							class="item__header__left line-clamp-1"
							:title="item?.[`title_${currentLang}`] || item?.title_cn"
						>
							{{ item?.[`title_${currentLang}`] || item?.title_cn }}
						</div>
						<div
							class="item__header__right text-right"
							style="flex: 1;"
						>
							{{ item?.salary }}
						</div>
					</div>
					
					<div class="flex-between" style="margin-top: 15px;">
						<div class="flex">
							<div class="info">
								<div class="info__value">
									{{ item?.number_recruits }}{{ $t('contact.job.numberRecruitsUnit') }}
								</div>
							</div>
							
							<div class="info">
								<div class="info__value">
									{{ item?.[`region_${currentLang}`] }}
								</div>
							</div>
							
							<div class="info">
								<div class="info__value">
									{{ dayjs(item?.end_data).format('YYYY-MM-DD') }}
								</div>
							</div>
						</div>
						
						<div
							class="item__header__right"
							:class="{
								'item__header__right--open': item.id === currentCollapse
							}"
							@click="handleChangeCollapse(item.id)"
						>
							<SvgIcon
								:name="item.id === currentCollapse ? 'circleArrow-white' : 'circleArrow'"
								class-name="icon"
							/>
						</div>
					</div>
				</div>
				
				<div
					class="item__content"
					:class="{
						'item__content--open': item.id === currentCollapse
					}"
				>
					
					<div
						class="block"
						v-for="(block, bIndex) in generateJobContentBlocks(item)"
						:key="bIndex"
					>
						<template v-if="isHaveJobInfo(block?.blocks)">
							<div class="block__title">
								{{ $t(block.title) }}:
							</div>
							<div class="block__list">
								<div
									class="block__list__item"
									v-for="(info, iIndex) in block.blocks"
								>
									（{{ iIndex + 1 }}）{{ info }}
								</div>
							</div>
						</template>
					</div>
					
					<div style="display: flex;">
						<MoreButton
							custom-class="resume-btn"
							:btn-text="$t('contact.job.resume')"
							icon-color="#DA241BFF"
							@click="handleUploadResume(item.id)"
						/>
					</div>
				</div>
				
			</div>
			
		</div>
		
		<div v-else>
			<el-empty
				style="padding: 20px 0 0 0;"
				:description="$t('contact.job.emptyDesc')"
			/>
		</div>
		
	</div>
	
	<ResumeUploadDialog
		v-model:visible="dialogVisible"
		:width="dialogWidth"
		:close-on-click-modal="false"
		:show-close="false"
		:position-id="position"
	/>
</template>

<style scoped lang="less">
.job {
	
	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		
		.right {
			display: flex;
			align-items: center;
			
			> div {
				margin-right: 48px;
				
				&:last-child {
					margin-right: 0;
				}
			}
			
			&__type {
				cursor: pointer;
				font-size: 24px;
				color: #3D3D3D;
				transition: all .5s;
				box-sizing: border-box;
				
				&:hover {
					color: @primary-color;
				}
				
				&--active {
					font-weight: bold;
				}
			}
			
			&__search {
				width: 240px;
			}
		}
	}
	
	&__list {
		margin-top: 48px;
		
		.item {
			margin-bottom: 24px;
			
			&:last-child {
				margin-bottom: 0;
			}
			
			&__header {
				display: flex;
				justify-content: space-between;
				background-color: #fff;
				padding: 32px;
				color: #3D3D3D;
				transition: all .3s;
				
				&--mobile {
					display: none;
				}
				
				&--open {
					color: #fff;
					background: linear-gradient(275deg, #D1170E 3%, #F46760 100%), linear-gradient(275deg, #DA241B 0%, #F0554D 100%);
					
					.info__value {
						color: #fff !important;
					}
				}
				
				&__left {
					width: 30%;
					font-size: 24px;
					line-height: normal;
				}
				
				.info {
					display: flex;
					align-items: center;
					
					&__value {
						margin-left: 8px;
						font-size: 18px;
						color: #606266;
					}
				}
				
				&__center {
					width: 55%;
					display: flex;
					justify-content: space-between;
				}
				
				&__right {
					transition: all .3s;
					&--open {
						transform: rotate(90deg);
					}
					.icon {
						width: 32px;
						height: 32px;
					}
					
					&--mobile {
						display: none;
					}
				}
			}
			
			&__content {
				padding: 0 0;
				background-color: #fff;
				height: 0;
				overflow: hidden;
				transform: scaleY(0);
				transform-origin: top;
				transition: all .3s;
				
				&--open {
					padding: 48px 32px;
					height: auto;
					transform: scaleY(1);
				}
				
				.block {
					margin-bottom: 32px;
					
					&:last-child {
						margin-bottom: 0;
					}
					
					&__title {
						font-size: 18px;
						color: #909399;
					}
					
					&__list{
						margin-top: 12px;
						&__item {
							font-size: 18px;
							color: #303133;
							line-height: 30px;
						}
						
					}
				}
				
				.resume-btn {
					border-color: @primary-color;
					background: linear-gradient(288deg, #D1170E 3%, #F46760 99%), linear-gradient(288deg, #DA241B -2%, #F0554D 99%);
					-webkit-background-clip: text;
					-webkit-text-fill-color: transparent;
					background-clip: text;
					text-fill-color: transparent;
				}
			}
			
		}
		
	}
	
	::v-deep(.el-input__wrapper) {
		border-radius: 77px;
		box-shadow: 0 0 0 1px #fff;
	}
	
	::v-deep(.is-focus) {
		box-shadow: 0 0 0 1px @primary-color;
	}
}

@media only screen and (max-width: 1024px) {
	.job {
		
		&__header {
			
			&__title {
				display: none;
			}
			
			.right {
				
				> div {
					margin-right: 20px;
				}
				
				&__type {
					font-size: 18px;
				}
				
				&__search {
					width: 178px;
				}
			}
			
		}
		
		&__list {
			margin-top: 20px;
			
			.item {
				margin-bottom: 20px;
				
				&__header {
					padding: 20px;
					display: block;
					
					&--open {
					
						.item__header__right {
							color: #fff;
						}
					}
					
					&--pc {
						display: none;
					}
					
					&--mobile {
						display: block;
					}
					
					.info {
						margin-right: 16px;
						
						&:last-child {
							margin-right: 0;
						}
						
						&__value {
							margin-left: 0;
							font-size: 14px;
						}
					}
					
					&__left {
						width: 45%;
						font-size: 18px;
					}
					
					&__center {
						display: none;
					}
					
					&__right {
						font-size: 18px;
						color: #303133;
					}

				}
				
				&__content {
					
					&--open {
						padding: 20px;
					}
					
					.block {
						margin-bottom: 20px;
						
						.block__list__item {
							font-size: 16px;
							line-height: 28px;
						}
					}
				}
			}
		}
	}
}

@media only screen and (max-width: 500px) {
	.job {
		
		&__header {
			
			.right {
				flex-wrap: wrap;
				> div {
					margin-right: 10px;
					padding: 10px 0;
				}
				
				&__type {
					font-size: 16px;
				}
				
				&__search {
					width: 178px;
				}
			}
			
		}
	}
}
</style>